import { useMemo, useState } from 'react';
import { type RaceWithParticipants } from '@/backend/domain/race/getRace';
import { formatRaceTime } from '@/lib/utils';
import { SortableTable } from '@/components/shared/tables/sortableTable';
import type { SortConfig } from '@/components/shared/tables/types';
import { Trophy } from 'lucide-react';

interface ParticipantTableProps {
    participants: RaceWithParticipants['participants'];
    showResults?: boolean;
    showTeam?: boolean;
    hideGender?: boolean;
    isTeamMemberList?: boolean;
    groupByGender?: boolean;
    groupByClass?: boolean;
    initialSort?: string;
    className?: string;
    showPlaceOfLiving?: boolean;
    showDisciplines?: boolean;
}

type Participant = RaceWithParticipants['participants'][0];

interface ParticipantWithPlacement extends Participant {
    computedPlace: number | null;
}

function calculatePlacementsByGroup(
    participants: Participant[],
    groupKey: (participant: Participant) => string
): ParticipantWithPlacement[] {
    const grouped = participants.reduce((groups, participant) => {
        const key = groupKey(participant);
        if (!groups[key]) {
            groups[key] = [];
        }
        groups[key].push(participant);
        return groups;
    }, {} as Record<string, typeof participants>);

    return participants.map(participant => {
        if (!participant.raceTime) return { ...participant, computedPlace: null };

        const group = grouped[groupKey(participant)];
        const sortedGroup = group
            .filter(p => p.raceTime && p.raceTime > 0)
            .sort((a, b) => (a.raceTime || 0) - (b.raceTime || 0));

        const place = sortedGroup.findIndex(p => p.id === participant.id) + 1;

        return {
            ...participant,
            computedPlace: place > 0 ? place : null
        };
    });
}

export function ParticipantTable({
                                     participants,
                                     showResults = false,
                                     showTeam = false,
                                     hideGender = false,
                                     isTeamMemberList = false,
                                     groupByGender = false,
                                     groupByClass = false,
                                     initialSort = 'raceTime',
                                     className,
                                     showPlaceOfLiving = false,
                                     showDisciplines = false
                                  }: ParticipantTableProps) {
    const initialDirection = initialSort === 'raceTime' ? 'asc' : 'asc';

    const [sortConfig, setSortConfig] = useState<SortConfig>(() => ({
        key: initialSort,
        direction: initialDirection
    }));

    const participantsWithPlacements = useMemo<ParticipantWithPlacement[]>(() => {
        if (!showResults || isTeamMemberList) {
            return participants.map(p => ({ ...p, computedPlace: null }));
        }

        if (groupByGender) {
            return calculatePlacementsByGroup(
                participants,
                (p) => p.gender === 'male' ? 'Männlich' : 'Weiblich'
            );
        }

        if (groupByClass) {
            return calculatePlacementsByGroup(
                participants,
                (p) => p.raceClass || 'Keine Klasse'
            );
        }

        return calculatePlacementsByGroup(participants, () => 'overall');
    }, [participants, showResults, isTeamMemberList, groupByGender, groupByClass]);

    const hasRaceClasses = useMemo(() =>
            participants.some(p => p.raceClass),
        [participants]
    );

    const columns = useMemo(() => {
        const cols = [];

        if (showResults && !isTeamMemberList) {
            cols.push({
                key: 'placement',
                header: 'Platz',
                sortable: true,
                cell: (participant: ParticipantWithPlacement) => {
                    const placement = participant.computedPlace;
                    return (
                        <div className="flex items-center gap-2">
                            {placement === 1 && <Trophy className="h-4 w-4 text-yellow-500" />}
                            {placement === 2 && <Trophy className="h-4 w-4 text-slate-400" />}
                            {placement === 3 && <Trophy className="h-4 w-4 text-amber-700" />}
                            {placement || '-'}
                        </div>
                    );
                }
            });
        }

        cols.push({
            key: 'startNumber',
            header: 'Startnr.',
            sortable: true,
            cell: (participant: ParticipantWithPlacement) =>
                participant.startNumber || '-'
        });

        cols.push({
            key: 'name',
            header: 'Name',
            sortable: true,
            cell: (participant: ParticipantWithPlacement) =>
                `${participant.firstName} ${participant.lastName}`
        });

        if (!hideGender && !groupByGender) {
            cols.push({
                key: 'gender',
                header: 'Geschlecht',
                sortable: true,
                cell: (participant: ParticipantWithPlacement) =>
                    participant.gender === 'male' ? 'M' : 'W'
            });
        }

        cols.push({
            key: 'birthYear',
            header: 'Jahrgang',
            sortable: true,
            cell: (participant: ParticipantWithPlacement) =>
                participant.birthYear
        });

        if (showPlaceOfLiving) {
            cols.push({
                key: 'place_of_living',
                header: 'Wohnort',
                sortable: true,
                cell: (participant: ParticipantWithPlacement) =>
                    participant.place_of_living || '-'
            });
        }

        if (showTeam && !isTeamMemberList) {
            cols.push({
                key: 'teamName',
                header: 'Team',
                sortable: true,
                cell: (participant: ParticipantWithPlacement) =>
                    participant.teamName || '-'
            });
        }

        if (showDisciplines) {
            cols.push({
                key: 'disciplines',
                header: 'Disziplin',
                sortable: true,
                cell: (participant: ParticipantWithPlacement) => {
                    const disciplines = participant.disciplines || ['SKI'];
                    if (disciplines.includes('SKI') && disciplines.includes('SNOWBOARD')) {
                        return 'Beide';
                    }
                    return disciplines.includes('SNOWBOARD') ? 'Snowboard' : 'Ski';
                }
            });
        }

        if (showResults) {
            if (!isTeamMemberList && hasRaceClasses && !groupByClass) {
                cols.push({
                    key: 'raceClass',
                    header: 'Klasse',
                    sortable: true,
                    cell: (participant: ParticipantWithPlacement) =>
                        participant.raceClass || '-'
                });
            }

            cols.push({
                key: 'raceTime',
                header: 'Zeit',
                sortable: true,
                cell: (participant: ParticipantWithPlacement) => (
                    <span className="font-mono">
                        {participant.raceTime ? (
                            formatRaceTime(participant.raceTime)
                        ) : participant.disqualifiedReason ? (
                            <span className="text-yellow-600">
                                DQ ({participant.disqualifiedReason})
                            </span>
                        ) : (
                            <span className="text-yellow-600">DNF</span>
                        )}
                    </span>
                )
            });
        }

        return cols;
    }, [showResults, showTeam, hideGender, isTeamMemberList, hasRaceClasses, groupByGender, groupByClass, showPlaceOfLiving, showDisciplines]);

    const handleSort = (key: string) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const sortedData = useMemo(() => {
        return [...participantsWithPlacements].sort((a, b) => {
            const key = sortConfig.key;
            let aValue: string | number = '';
            let bValue: string | number = '';

            if (key === 'name') {
                aValue = `${a.lastName} ${a.firstName}`.toLowerCase();
                bValue = `${b.lastName} ${b.firstName}`.toLowerCase();
            } else if (key === 'placement') {
                aValue = a.computedPlace || Number.MAX_SAFE_INTEGER;
                bValue = b.computedPlace || Number.MAX_SAFE_INTEGER;
            } else if (key === 'raceTime') {
                aValue = a.raceTime || Number.MAX_SAFE_INTEGER;
                bValue = b.raceTime || Number.MAX_SAFE_INTEGER;
            } else {
                if (key === 'disciplines') {
                    const mapDisciplines = (participant: ParticipantWithPlacement) => {
                        const disciplines = participant.disciplines || ['SKI'];
                        if (disciplines.includes('SKI') && disciplines.includes('SNOWBOARD')) {
                            return 'beide';
                        }
                        return disciplines.includes('SNOWBOARD') ? 'snowboard' : 'ski';
                    };
                    aValue = mapDisciplines(a);
                    bValue = mapDisciplines(b);
                } else if (key === 'startNumber' || key === 'birthYear') {
                    aValue = Number(a[key as keyof typeof a] || 0);
                    bValue = Number(b[key as keyof typeof b] || 0);
                } else {
                aValue = String(a[key as keyof typeof a] || '');
                bValue = String(b[key as keyof typeof b] || '');
                }
            }

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return sortConfig.direction === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return sortConfig.direction === 'asc'
                ? (aValue as number) - (bValue as number)
                : (bValue as number) - (aValue as number);
        });
    }, [participantsWithPlacements, sortConfig]);

    return (
        <SortableTable
            data={sortedData}
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            className={className}
        />
    );
}
